import {Select, DatePicker, Button, Tooltip, Tag, Dropdown} from 'antd';
import {LoadingOutlined, PlusOutlined} from '@ant-design/icons';
import moment from 'moment';
import dayjs, {Dayjs} from 'dayjs';
import React, {useMemo, useState} from 'react';

import {getCampaignTypes, CONSTANTS, brandConfig} from '../../utils';
import './connectionsFilter.scss';
import {ConnectionsTableColumnKeys} from '../../../../types/src';

const {Option} = Select;
const {RangePicker} = DatePicker;

const dateOptions = [
  {
    label: 'Lifetime',
    value: 'lifetime',
  },
  {
    label: 'Last 30 days vs last previous 30 days',
    value: 'last_30_days_vs_prev_30_days',
    className: 'connections-page__last30vsprev30-option',
  },
  {
    label: 'Last 7 days',
    value: 'last_7_days',
  },
  {
    label: 'Last 30 days',
    value: 'last_30_days',
  },
  {
    label: 'Last 3 Months',
    value: 'last_3_months',
  },
  {
    label: 'Last 6 months',
    value: 'last_6_months',
  },
  {
    label: 'Last 12 months',
    value: 'last_12_months',
  },
  {
    label: 'Custom',
    value: 'custom',
  }
];

enum FilterType {
  User = 'user',
  Campaign = 'campaign',
  CampaignType = 'campaign_type',
  Clicked = 'clicked',
  Responded = 'responded',
}

const possibleFilters = [
  {label: 'Users', key: FilterType.User},
  {label: 'Campaigns', key: FilterType.Campaign},
  {label: 'Type', key: FilterType.CampaignType},
  {label: 'Clicked', key: FilterType.Clicked},
  {label: 'Responded', key: FilterType.Responded},
];

const CAMPAIGN_TYPES = [
  CONSTANTS.campaignTypes.acquisition,
  CONSTANTS.campaignTypes.acquisition_csv,
  CONSTANTS.campaignTypes.acquisition_via_import,
  CONSTANTS.campaignTypes.retention,
  CONSTANTS.campaignTypes.retention_csv,
  CONSTANTS.campaignTypes.retention_actions,
  CONSTANTS.campaignTypes.retention_tags,
  CONSTANTS.campaignTypes.acquisition_inmail,
];

const getSearchableFiltersColumns = () => {
  return CONSTANTS.connectionsTableColumns.filter(
    v => ![
      ConnectionsTableColumnKeys.User,
      ConnectionsTableColumnKeys.Campaigns,
      ConnectionsTableColumnKeys.ImportedDate,
      ConnectionsTableColumnKeys.ClickedLinks]
      .includes(v.value)
  );
}

const getActiveFilters = (filters) => {
  const initialFilters = [];
  if (filters?.user?.length) initialFilters.push(FilterType.User);
  if (filters?.campaign?.length) initialFilters.push(FilterType.Campaign);
  if (filters?.campaign_type?.length) initialFilters.push(FilterType.CampaignType);
  if (filters?.clicked) initialFilters.push(FilterType.Clicked);
  if (filters?.responded) initialFilters.push(FilterType.Responded);
  return initialFilters;
}

export const ConnectionsFilter = ({
  connections,
  users,
  campaigns,
  setFilters,
  filters,
  totalConnections,
  selectedRows,
  connectionsLoading,
}: any) => {
  const [showCustom, setShowCustom] = useState(false);
  const [shownFilters, setShownFilters] = useState(getActiveFilters(filters));
  const [showDatesSelect, setShowDatesSelect] = useState(false);
  const searchableFiltersColumns = getSearchableFiltersColumns();
  const searchableFilters = useMemo(() => {
    const searchFilters = searchableFiltersColumns.map(({label, value}) => {
      if (filters[value]?.[0]) {
        return {label: `${label}: ${filters[value]?.[0]}`, key: value}
      }
      return null;
    });
    return searchFilters.filter(v => v);
  }, [filters, searchableFiltersColumns]);

  const filterCampaigns = filters?.user?.length
    ? campaigns.filter(c => filters.user.includes(c.selectedUser?.id))
    : campaigns;

  const lastTwoMonthRange: [Dayjs, Dayjs] = [dayjs().subtract(1, 'months'), dayjs()];
  const disableFutureDates = currentDate => currentDate && currentDate >= moment().endOf('day');
  const filterMenu = useMemo(() => possibleFilters.filter(f => !shownFilters.includes(f.key)), [shownFilters]);

  const handleUsersChange = user => setFilters({...filters, user, start: 0});
  const handleCampaignsChange = campaign => setFilters({...filters, campaign, start: 0});
  const handleCampaignsChangeType = campaign_type => setFilters({...filters, campaign_type, start: 0});
  const addClickedFilter = clicked => setFilters({...filters, clicked, start: 0});
  const addRespondedFilter = responded => setFilters({...filters, responded, start: 0});

  const onDeleteFilter = (key: string) => {
    const newFilters = {...filters};
    delete newFilters[key];
    setFilters(newFilters);
  }

  const removeSearchFilter = () => {
    const newFilters = {...filters};
    delete newFilters.receiver_id;
    setFilters(newFilters);
  };

  const handleDateChange = date => {
    if (date === 'custom') {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    setFilters({
      ...filters,
      date,
      date_range_start: '',
      date_range_end: '',
      start: 0,
    });
  };

  const onAddFilter = ({key}) => {
    switch (key) {
      case FilterType.Responded: {
        addRespondedFilter(1);
        break;
      }
      case FilterType.Clicked: {
        addClickedFilter(1);
        break;
      }
    }
    setShownFilters(v => [...v, key]);
  }

  const onRemoveFilter = (key) => {
    switch (key) {
      case FilterType.Responded: {
        onDeleteFilter(FilterType.Responded);
        break;
      }
      case FilterType.Clicked: {
        onDeleteFilter(FilterType.Clicked);
        break;
      }
    }
    setShownFilters(v => v.filter(filter => filter !== key));
  }

  const customDates = arg => {
    setShowCustom(false);
    setShowDatesSelect(false);
    let [start, end] = arg;
    start = start.format('YYYY-MM-DD');
    end = end.format('YYYY-MM-DD');
    setFilters({
      ...filters,
      date: `${start} - ${end}`,
      date_range_start: start,
      date_range_end: end,
      start: 0,
    });
  };

  const renderFilters = (filter) => {
    switch (filter) {
      case FilterType.User:
        return (
          <Select
            key={FilterType.User}
            loading={connectionsLoading}
            mode="multiple"
            placeholder="Select users"
            onChange={handleUsersChange}
            value={users.length > 0 ? filters?.user : undefined}
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="connections-page__filter-input"
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>
                {user.first_name ? `${user.first_name} ${user.last_name}` : user.email}
              </Option>
            ))}
          </Select>
        )
      case FilterType.Campaign:
        return (
          <Select
            key={FilterType.Campaign}
            loading={connectionsLoading}
            mode="multiple"
            placeholder="Select campaign"
            onChange={handleCampaignsChange}
            value={filterCampaigns.length > 0 ? filters?.campaign : undefined}
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="connections-page__filter-input"
          >
            {[...filterCampaigns].map((c: any) => (
              <Option key={c.id}>{c.name}</Option>
            ))}
          </Select>
        )
      case FilterType.CampaignType:
        return (
          <Select
            key={FilterType.CampaignType}
            mode="multiple"
            placeholder="Select type"
            onChange={handleCampaignsChangeType}
            value={filters?.campaign_type}
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="connections-page__filter-input"
          >
            {CAMPAIGN_TYPES.map(type => (
              <Option key={type} className="ghp">
                {getCampaignTypes(type).value}
              </Option>
            ))}
          </Select>
        )
      case FilterType.Responded:
        return (
          <Tooltip title="Show only connections who responded" key={FilterType.Responded}>
            <Tag
              closable={true}
              color={brandConfig.primaryColor}
              onClose={() => onRemoveFilter(FilterType.Responded)}
            >
              Responded
            </Tag>
          </Tooltip>
        )
      case FilterType.Clicked:
        return (
          <Tooltip title="Show only connections who clicked on the link" key={FilterType.Clicked}>
            <Tag
              closable={true}
              color={brandConfig.primaryColor}
              onClose={() => onRemoveFilter(FilterType.Clicked)}
            >
              Clicked
            </Tag>
          </Tooltip>
        )
    }
  }

  return (
    <div className="connections-page__filters">
      <div>
        <RangePicker
          className="custom-range__datepicker"
          open={showCustom}
          onChange={customDates}
          defaultValue={lastTwoMonthRange}
          disabledDate={disableFutureDates}
        />
        <Select
          open={showDatesSelect}
          value={filters?.date}
          className="connections-page__filter-input"
          onDropdownVisibleChange={open => setShowDatesSelect(open)}
          onChange={handleDateChange}
        >
          {dateOptions.map(item => (
            <Option className={item.className} key={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
      </div>

      {shownFilters.map(renderFilters)}

      {searchableFilters.map(({label, key}) => (
        <Tag
          key={key}
          closable={true}
          color={brandConfig.primaryColor}
          onClose={() => onDeleteFilter(key)}
        >
          {label}
        </Tag>
      ))}

      {filters?.receiver_id && (
        <Tooltip title={filters.receiver_id}>
          <Tag
            key="receiver_id"
            closable={true}
            color={brandConfig.primaryColor}
            onClose={removeSearchFilter}
          >
            {filters.receiver_id}
          </Tag>
        </Tooltip>
      )}

      <Dropdown
        menu={{items: filterMenu, onClick: onAddFilter}}
        trigger={['click']}
        placement="bottom"
        disabled={filterMenu.length === 0}
      >
        <Button type="link" icon={<PlusOutlined />}>
          Add filter
        </Button>
      </Dropdown>

      <span className="connections-page__selected-count">
        {connectionsLoading ? (
          <span>
            <LoadingOutlined /> loading...
          </span>
        ) : (
          `Showing ${connections?.length} of ${totalConnections} (${selectedRows?.length} Selected)`
        )}
      </span>
    </div>
  );
};
